import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token && token !== 'undefined' && token !== 'null') {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )

        // Add response interceptor to handle 401 errors
        apiInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    localStorage.removeItem('access_token')
                    localStorage.removeItem('user')
                    window.location.href = '/sign-in'
                }
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

export interface ProfileUpdateData {
    first_name?: string
    last_name?: string
    email?: string
}

export interface ChangePasswordData {
    current_password: string
    new_password: string
}

export interface NotificationPreferences {
    course_updates: boolean
    marketing_emails: boolean
    weekly_progress_report: boolean
}

export interface UserProfile {
    id: string
    email: string
    username: string
    first_name?: string
    last_name?: string
    role: string
    is_active: boolean
    created_at: string
    updated_at: string
}

export const accountService = {
    updateProfile,
    changePassword,
    getNotificationPreferences,
    updateNotificationPreferences
}

/**
 * Update user profile (first_name, last_name, and email)
 */
async function updateProfile(data: ProfileUpdateData): Promise<ApiResponse<UserProfile>> {
    try {
        const response = await getApiInstance().put('/users/me/profile', data)
        const responseData = response.data

        // Update user in localStorage if successful
        if (responseData.success && responseData.data) {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
            const updatedUser = {
                ...currentUser,
                first_name: responseData.data.first_name,
                last_name: responseData.data.last_name,
                email: responseData.data.email
            }
            localStorage.setItem('user', JSON.stringify(updatedUser))
        }

        return {
            success: true,
            data: responseData.data
        }
    } catch (error: any) {
        console.error('Update profile error:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to update profile'
        }
    }
}

/**
 * Change user password
 */
async function changePassword(data: ChangePasswordData): Promise<ApiResponse<{ changed: boolean }>> {
    try {
        const response = await getApiInstance().put('/users/me/password', data)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Change password error:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to change password'
        }
    }
}

/**
 * Get notification preferences
 */
async function getNotificationPreferences(): Promise<ApiResponse<NotificationPreferences>> {
    try {
        const response = await getApiInstance().get('/users/me/notifications')
        return {
            success: true,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Get notification preferences error:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to get notification preferences'
        }
    }
}

/**
 * Update notification preferences
 */
async function updateNotificationPreferences(data: NotificationPreferences): Promise<ApiResponse<NotificationPreferences>> {
    try {
        const response = await getApiInstance().put('/users/me/notifications', data)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Update notification preferences error:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to update notification preferences'
        }
    }
}
